import { AppContext } from "App";
import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { EDIT_CERTIFICATE } from "utils/permissions";
import CPSCProductsBatchUpdateForm from "./CPSCProductsBatchUpdateForm";
import { FETCH_CPSC_PRODUCTS } from "./graphql";

const CPSCProductsPage = () => {
  const { hasPermission } = useContext(AppContext);
  const canEditCpscProducts = hasPermission(EDIT_CERTIFICATE);
  const [searchParams, setSearchParams] = useSearchParams({ q: "" });
  const modal = useModals();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const q = searchParams.get("q") || "";
  const normalizedQ = q.trim().toLowerCase();
  const { loading, error, data } = useQuery(FETCH_CPSC_PRODUCTS, {
    fetchPolicy: "network-only",
  });

  const cpscProducts = data?.cpscProducts || [];
  const filteredProducts = cpscProducts.filter((item) => {
    if (!normalizedQ) return true;
    return (item.product?.number || "").toLowerCase().includes(normalizedQ);
  });
  const allFilteredSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((item) =>
      selectedProducts.some((selected) => selected.id === item.id),
    );

  function toggleSelectProduct(product, checked) {
    if (!checked) {
      setSelectedProducts((prev) =>
        prev.filter((item) => item.id !== product.id),
      );
    } else {
      setSelectedProducts((prev) =>
        prev.some((item) => item.id === product.id) ? prev : [...prev, product],
      );
    }
  }

  function toggleSelectAllFiltered(checked) {
    if (!checked) {
      const filteredIds = new Set(filteredProducts.map((item) => item.id));
      setSelectedProducts((prev) =>
        prev.filter((item) => !filteredIds.has(item.id)),
      );
      return;
    }

    const selectedIds = new Set(selectedProducts.map((item) => item.id));
    const newlySelected = filteredProducts.filter(
      (item) => !selectedIds.has(item.id),
    );
    setSelectedProducts((prev) => [...prev, ...newlySelected]);
  }

  function openBatchUpdateModal() {
    modal.present({
      title: "Update CPSC Products",
      isBeingPresented: true,
      center: true,
      children: (
        <CPSCProductsBatchUpdateForm
          products={selectedProducts}
          onCompleted={() => {
            setSelectedProducts([]);
            modal.hide();
          }}
        />
      ),
    });
  }

  return (
    <Page
      backTo="./.."
      title="CPSC Products"
      className="h-screen"
      rightButtons={
        canEditCpscProducts ? (
          <div>
            <a
              href={`${process.env.REACT_APP_SERVER_ADMIN_URL}compliance/cpscproduct/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Admin
            </a>
          </div>
        ) : null
      }
    >
      <div className="p-6 h-full flex flex-col">
        <div>
          <div>
            List of products that are registered with CPSC. Certificates are
            managed in the{" "}
            <a
              href="https://efiling.saferproducts.gov/efiling/"
              target="_blank"
              rel="noopener noreferrer"
            >
              CPSC EFiling System
            </a>{" "}
            by Stella.
          </div>
        </div>

        <div className="mt-4">
          <SearchBar
            initialQuery={q}
            placeholder="Search by product SKU"
            onChange={(nextQ) => setSearchParams({ q: nextQ })}
          />
        </div>

        <div className="card p-0 flex-1 overflow-auto flex flex-col mt-6">
          <div className="flex-1 overflow-auto relative">
            {loading ? (
              <Spinner />
            ) : error ? (
              <Errors error={error} />
            ) : filteredProducts.length === 0 ? (
              <div className="p-8 text-sm text-gray-500">
                No CPSC products found.
              </div>
            ) : (
              <div className="whitespace-nowrap flex flex-col h-full">
                <table className="w-full">
                  <thead>
                    <tr className="sticky top-0 backdrop-blur-xl dark:bg-opacity-90 z-10 bg-gray-50 dark:bg-gray-800">
                      <th colSpan={2} className="px-4 py-3 text-left">
                        <div className="flex items-center space-x-4">
                          {canEditCpscProducts ? (
                            <input
                              type="checkbox"
                              checked={allFilteredSelected}
                              onChange={(e) =>
                                toggleSelectAllFiltered(e.target.checked)
                              }
                            />
                          ) : null}
                          {selectedProducts.length > 0 ? (
                            <>
                              <span>
                                {selectedProducts.length} product
                                {selectedProducts.length > 1 ? "s" : ""}{" "}
                                selected
                              </span>
                              <Button onClick={openBatchUpdateModal}>
                                Update
                              </Button>
                              <Button
                                danger
                                onClick={() => setSelectedProducts([])}
                              >
                                Clear
                              </Button>
                            </>
                          ) : (
                            <span>
                              CPSC Products ({filteredProducts.length})
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-3 text-left">Version ID</th>
                      <th className="px-3 text-left">Certifier ID</th>
                      <th className="px-3 text-left">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((item) => (
                      <tr
                        key={item.id}
                        className="border-y border-gray-100 dark:border-gray-700"
                      >
                        <td colSpan={2} className="px-4 py-2">
                          <div className="flex items-center space-x-4">
                            {canEditCpscProducts ? (
                              <input
                                type="checkbox"
                                checked={selectedProducts.some(
                                  (selected) => selected.id === item.id,
                                )}
                                onChange={(e) =>
                                  toggleSelectProduct(item, e.target.checked)
                                }
                              />
                            ) : null}
                            <div>
                              <div>
                                <span className="font-semibold">
                                  {item.product?.number || "-"}
                                </span>
                              </div>
                              <div className="opacity-70">
                                {item.product?.name || "-"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3">{item.versionId || "-"}</td>
                        <td className="px-3">{item.certifierId || "-"}</td>
                        <td className="px-3 whitespace-pre-wrap">
                          {item.note}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default CPSCProductsPage;
